import React, { useEffect, useRef } from "react";
import CONSTANT from "../../../../constants/Constant";
import CheckboxOption from "../../form-element/CheckboxOption";
import RadioBtnComponent from "./RadioBtnComponent";
import { getMessageByKeyTexts, playAudio, shouldCheckboxOptionDisabled, similarity } from "../../../../utils/Utils";
import { useAzureSpeech } from "../utilities/useAzureSpeech";

const PainQuestion = ({
                          painQuestion,
                          onChangeAnswer,
                          isClickedOnSubmitBtn,
                          isSpeechEnabled,
                          waitingTime,
                          realTimeDetectionConstrains,
                      }) => {
    const isCheckBox = painQuestion?.QuestionType === CONSTANT.ELEMENT_TYPES.CHECKBOX;
    const answerList = painQuestion?.AnswerList || [];
    const singleSelectAnswerId = painQuestion?.SingleSelectAnswerId || 0;
    const responses = painQuestion?.Responses || [];

    const { startListening, stopListening, isListening, transcript, lastError, countdown } =
        useAzureSpeech();

    const [isDontMatch, setIsDontMatch] = React.useState(false);
    const [repeatCount, setRepeatCount] = React.useState(0);
    const [flagForAudio, setFlagForAudio] = React.useState(false);
    const [isAnswerReceiving, setIsAnswerReceiving] = React.useState(false);
    const [retryAttempt, setRetryAttempt] = React.useState(0);
    const [isAudioPlaying, setIsAudioPlaying] = React.useState(false);
    const [showListeningText, setShowListeningText] = React.useState(false);

    // refs for robust sequencing
    const isMountedRef = useRef(true);
    const matchedRef = useRef(false); // true if this listening session matched an answer
    const prevIsListeningRef = useRef(false); // previous isListening state to detect transition
    const retryTimeoutRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const isAnswered = (q) => (isCheckBox ? answerList?.length > 0 : !!q?.AnswerId);
    const isCurrentAnswerChecked = (answerList, currentAnswerId) =>
        (isCheckBox && answerList.includes(currentAnswerId)) || false;

    const handleOnClicked = (question, response) => {
        setIsDontMatch(false);
        if (
            isCheckBox &&
            shouldCheckboxOptionDisabled(singleSelectAnswerId, answerList, response?.AnswerId)
        ) {
            return;
        }

        if (isSpeechEnabled) {
            playAudio("remove");
            stopListening();
        }

        onChangeAnswer(question, response?.AnswerId);
    };

    const getRetryVoice = () => {
        const keyText = retryAttempt === 0 ? "1stvoice" : "2ndvoice";
        const { audio } =
            getMessageByKeyTexts(realTimeDetectionConstrains, "BiometricVoiceInteraction", keyText) ||
            {};
        console.log(`Retry attempt ${retryAttempt + 1}:`, audio);
        return audio || "";
    };

    // handle retry (plays retry audio, toggles flag to trigger playAll for next round)
    const handleRetry = async () => {
        if (isAudioPlaying) return; // don't run retry while another audio is playing
        stopListening(); // ensure recognizer stopped
        setIsDontMatch(true);
        setShowListeningText(false);

        const retryAudio = getRetryVoice();

        if (retryAudio) {
            try {
                setIsAudioPlaying(true);
                await playAudio(retryAudio, { priorityLevel: 2 });
            } catch (err) {
                console.error("Retry audio playback failed:", err);
            } finally {
                if (isMountedRef.current) setIsAudioPlaying(false);
            }
        }

        if (!isMountedRef.current) return;

        setRetryAttempt((prev) => Math.min(prev + 1, 1)); // limit to 2 stages
        setFlagForAudio((prev) => !prev);
    };

    // sequential playback of question + responses, then start listening
    const playAll = async () => {
        if (isAudioPlaying) return; // prevent overlap
        // if already listening or answer in-flight, do not begin playback
        if (isListening || isAnswerReceiving) return;

        try {
            setIsAudioPlaying(true);
            setShowListeningText(false);

            // reset matchedRef for the upcoming listening session
            matchedRef.current = false;
            setIsAnswerReceiving(false);
            setIsDontMatch(false);

            // Start listening immediately before playing the question audio
            const listeningPromise = startListening({ language: "en-US", timeoutSeconds: waitingTime });

            // play question voice (if exists)
            if (painQuestion?.VoiceUrl) {
                await playAudio(painQuestion.VoiceUrl, { priorityLevel: 2 });
            }

            if (!isMountedRef.current) return;

            // Show listening text after the question audio finishes
            setShowListeningText(true);

            // increment repeat count after full play
            setRepeatCount((prev) => prev + 1);

            // Wait for listening to complete
            await listeningPromise;

            if (!isMountedRef.current) return;

            // ensure any stray audio remove command runs after startListening
            playAudio("remove");
        } catch (err) {
            console.error("Audio playback error:", err);
        } finally {
            if (isMountedRef.current) setIsAudioPlaying(false);
        }
    };

    // Trigger initial play when flagForAudio toggles and speech is enabled
    useEffect(() => {
        if (!isSpeechEnabled) return;

        // only play if repeats not exhausted
        if (repeatCount < 2) {
            // ensure we call playAll, but it will early-return if conditions not right
            playAll();
        } else {
            // we've reached max repeats
            console.log("finished due to repeated count");
            playAudio("remove");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagForAudio, isSpeechEnabled]);

    // Analyze transcript when it arrives and set matchedRef if matched
    useEffect(() => {
        if (!isSpeechEnabled || !transcript) return;

        console.log("=== Transcript received ===", transcript);

        const words = transcript
            .toLowerCase()
            .split(/[,.\s]+/)
            .map((w) => w.trim())
            .filter(Boolean);

        const threshold = 0.5;
        let matched = false;

        if (isCheckBox) {
            console.log("Question type: Checkbox, fuzzy matching multiple answers");
            responses.forEach((response) => {
                const answerText = (response.AnswerTitle || "").toLowerCase();
                const bestMatch = words.some((word) => similarity(word, answerText) >= threshold);
                const isAlreadyChecked = answerList.includes(response.AnswerId);

                if (bestMatch && !isAlreadyChecked) {
                    matched = true;
                    // mark matched for this listening session
                    matchedRef.current = true;
                    playAudio("remove");
                    setIsAnswerReceiving(true);
                    handleOnClicked(painQuestion, response);
                }
            });
        } else {
            console.log("Question type: Radio, fuzzy matching single answer");
            if (!isAnswered(painQuestion)) {
                const matchedResponse = responses.find((response) => {
                    const answerText = (response.AnswerTitle || "").toLowerCase();
                    return words.some((word) => similarity(word, answerText) >= threshold);
                });

                if (matchedResponse) {
                    matched = true;
                    matchedRef.current = true;
                    playAudio("remove");
                    setIsAnswerReceiving(true);
                    handleOnClicked(painQuestion, matchedResponse);
                }
            }
        }

        // NOTE: If transcript exists but no match found, we do NOT call handleRetry here.
        // Instead, we rely on the listening -> not-listening transition effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transcript, isListening]);

    // Detect transition isListening: true -> false and trigger retry only if nothing matched
    useEffect(() => {
        if (!isSpeechEnabled) return;

        const prev = prevIsListeningRef.current;
        const curr = !!isListening;
        prevIsListeningRef.current = curr; // update for next render

        // if we transitioned from listening -> not listening
        if (prev === true && curr === false) {
            // clear any existing retry timeout
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }

            // small debounce to allow transcript effect to run and set matchedRef (if it will)
            retryTimeoutRef.current = setTimeout(() => {
                retryTimeoutRef.current = null;

                // only retry if no answer was matched in the last listening session
                const answeredNow = isAnswered(painQuestion) || isAnswerReceiving || matchedRef.current;
                if (!answeredNow) {
                    console.log("Listening timed out and no match -> triggering retry");
                    handleRetry();
                } else {
                    // reset matchedRef if answered
                    matchedRef.current = false;
                }
            }, 250); // 250ms gives transcript effect time to complete
        }

        // cleanup on unmount
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isListening, isSpeechEnabled, isAnswerReceiving, repeatCount]);

    // Cleanup matchedRef when question changes (so stale matches don't persist)
    useEffect(() => {
        matchedRef.current = false;
        prevIsListeningRef.current = false;
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }
    }, [painQuestion?.QuestionId]);

    return (
        <div
            className="mb-3 pain-question-modal-question"
            id={`pain-question-${painQuestion.QuestionId}`}
        >
            <div className="d-flex align-items-center justify-content-between">
                <label className="pain-question-modal-question__title">
                    {painQuestion.QuestionTitle}
                </label>
            </div>

            {/* Speech Controls */}
            {isSpeechEnabled && (
                <div className="my-2">
                    {/*<div>{countdown}</div>*/}
                    {isListening && showListeningText && (
                        <span style={{ color: "#0075B6" }} className="text-success font-size-1_3rem">🔊 Listening...</span>
                    )}
                    {/*{isDontMatch && (*/}
                    {/*    <div className="text-red-500">*/}
                    {/*        Sorry, I didn't catch that. If persists please click to choose your*/}
                    {/*        answer!*/}
                    {/*    </div>*/}
                    {/*)}*/}
                    {lastError && (
                        <div className="text-red-500 text-danger font-size-1_3rem">
                            An error occurred. Please try again or click to choose your answer!
                        </div>
                    )}
                </div>
            )}

            {/* Response Options */}
            <div className="d-flex flex-wrap padding-top-1-rem padding-bottom-1-rem">
                {responses.map((response, key) =>
                    isCheckBox ? (
                        <CheckboxOption.OptionItem
                            key={response?.AnswerId || key}
                            isChecked={isCurrentAnswerChecked(answerList, response?.AnswerId)}
                            onClick={() => handleOnClicked(painQuestion, response)}
                            label={response?.AnswerTitle}
                            className={`kt-radio kt-radio--bold kt-radio--brand mr-3 pain-question-modal-question__options white-space-no-wrap margin-bottom-1-rem ${isCurrentAnswerChecked(answerList, response?.AnswerId)
                                    ? "selected-option"
                                    : ""
                                }`}
                            iconClassName="mr-3"
                            disabled={shouldCheckboxOptionDisabled(
                                singleSelectAnswerId,
                                answerList,
                                response?.AnswerId
                            )}
                        />
                    ) : (
                        <RadioBtnComponent
                            className="kt-radio kt-radio--bold kt-radio--brand mr-3 pain-question-modal-question__options white-space-no-wrap"
                            type={
                                isCheckBox
                                    ? CONSTANT.ELEMENT_TYPES.CHECKBOX
                                    : CONSTANT.ELEMENT_TYPES.BUTTON
                            }
                            key={response?.AnswerId || key}
                            name={`pain${painQuestion?.QuestionId || ""}`}
                            value={response?.AnswerId}
                            title={response?.AnswerTitle}
                            onClick={() => handleOnClicked(painQuestion, response)}
                            checked={
                                (isCheckBox && answerList.includes(response?.AnswerId)) ||
                                (!isCheckBox && response?.AnswerId === painQuestion?.AnswerId)
                            }
                        />
                    )
                )}
            </div>

            {isClickedOnSubmitBtn && !isAnswered(painQuestion) && (
                <div className="color-red d-block">* Please answer this question.</div>
            )}
        </div>
    );
};

export default PainQuestion;
